import React, { useCallback, useMemo, useState } from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import Search from '~/components/Search';
import api from '~/services/api';
import useAuth from '~/hooks/useAuth';
import { Container } from './styles';
import FormDefault from '~/components/FormDefault';
import ToggleDefault from '~/components/ToggleDefault';
import { InputNumber, InputSelect, InputText } from '~/components/NovosInputs';
import DefaultLoader from '~/components/DefaultLoader';

type Option = { value: number; label: string };

type RowStatusPDV = {
  cod_status_pdv: number | string;
  des_status_pdv: string;
  status_pdv: number;
  flg_negativar: boolean;
  tipo_condicao: number;
  tipo_finalizadora: number;
};

type FormValues = {
  cod_status?: number | string;
  des_status: string;
  val_status: number;
  negativar_status: boolean;
  indicador_status: any;
  tipo_status: any;
};

const options: any[] = [
  { value: 1, label: 'Aceitar normalmente' },
  { value: 2, label: 'Aceitar somente com autorização do supervisor' },
  { value: 3, label: 'Não pode ser aceito' },
];

const options_finalizadora: any[] = [
  { value: 1, label: 'Todos' },
  { value: 2, label: 'Cheque' },
  { value: 3, label: 'Convênio' },
];

const defaultValues: any = {
  des_status: '',
  val_status: 1,
  negativar_status: false,
  indicador_status: options[0],
  tipo_status: options_finalizadora[0],
};

const CadastroStatusPdv: React.FC = () => {
  const user = useAuth();
  const [loader, setLoader] = useState(false);
  const [showSearch, setShowSearch] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);

  const {
    control,
    register,
    handleSubmit: rhfSubmit,
    setValue,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<any>({
    mode: 'onChange',
    defaultValues,
  });

  const valStatusValue = watch('val_status');
  const negativar = watch('negativar_status');

  const forbiddenDescriptions = useMemo(
    () => [/^NENHUM$/i, /^NENHUMA$/i, /^NENHUM\(A\)$/i],
    [],
  );

  const hasForbiddenDescription = useCallback(
    (value: string) =>
      forbiddenDescriptions.some((re) => re.test(value.trim())),
    [forbiddenDescriptions],
  );

  const resetForm = useCallback(() => {
    reset(defaultValues, { keepDirty: false });
    setIsUpdate(false);

    const inputAceite = document.querySelector(
      '[name="indicador_status"]',
    ) as HTMLInputElement | null;

    if (inputAceite) inputAceite.value = '';

    const inputFinalizadora = document.querySelector(
      '[name="tipo_status"]',
    ) as HTMLInputElement | null;

    if (inputFinalizadora) inputFinalizadora.value = '';
  }, [reset]);

  const onRowClick = async (param: GridRowParams) => {
    const { row } = param as unknown as { row: RowStatusPDV };

    const indicadorOpt =
      options.find((o) => o.value === row.tipo_condicao) ?? options[0];
    const tipoOpt =
      options_finalizadora.find((o) => o.value === row.tipo_finalizadora) ??
      options_finalizadora[0];

    reset(
      {
        cod_status: row.cod_status_pdv,
        des_status: row.des_status_pdv ?? '',
        val_status: Number(row.status_pdv ?? 1),
        negativar_status: Boolean(row.flg_negativar),
        indicador_status: indicadorOpt,
        tipo_status: tipoOpt,
      },
      { keepDirty: false },
    );

    setIsUpdate(true);
    setShowSearch(false);
    setLoader(false);
  };

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (
        String(values.des_status).trim() === '' &&
        String(values.val_status).trim() === ''
      ) {
        setError('des_status', { type: 'required' });
        setError('val_status', { type: 'required' });
        return;
      }

      if (String(values.des_status).trim() === '') {
        setError('des_status', { type: 'required' });
        return;
      }

      if (String(values.val_status).trim() === '') {
        setError('val_status', { type: 'required' });
        return;
      }

      if (hasForbiddenDescription(values.des_status)) {
        toast.warn('Descrição contém palavra não permitida "Nenhum/Nenhuma".');
        const inputStatus = document.querySelector(
          '[name="des_status"]',
        ) as HTMLInputElement | null;

        if (inputStatus) {
          inputStatus.focus();
          inputStatus.select();
        }

        return;
      }

      setLoader(true);
      try {
        if (isUpdate && values.cod_status != null) {
          const { data } = await api.put(`/status-pdv/${values.cod_status}`, {
            des_status: values.des_status,
            val_status: values.val_status,
            negativar_status: values.negativar_status,
            indicador_status: values.indicador_status,
            tipo_status: values.tipo_status,
            cod_usuario_alteracao: user.user && user.user.id,
          });
          toast.success(data.message);
          resetForm();
          setShowSearch(true);
          return;
        }

        const { data } = await api.post('/status-pdv', {
          des_status: values.des_status,
          val_status: values.val_status,
          negativar_status: values.negativar_status,
          indicador_status: values.indicador_status,
          tipo_status: values.tipo_status,
          cod_usuario_cadastro: user.user && user.user.id,
        });
        toast.success(data.message);
        resetForm();
        setShowSearch(false);
      } finally {
        setLoader(false);
      }
    },
    [hasForbiddenDescription, isUpdate, resetForm, user?.user?.id],
  );

  const handleDelete = useCallback(async () => {
    const cod = watch('cod_status');
    if (cod == null) {
      toast.warn('Não há registro selecionado para excluir.');
      return;
    }

    setLoader(true);
    try {
      const res = await api.delete(`/status-pdv/${cod}`);
      const { success, message } = res.data;
      if (!success) throw new Error(message);
      resetForm();
      setShowSearch(true);
      toast.success(message);
    } finally {
      setLoader(false);
    }
  }, [resetForm, watch]);

  const onNew = useCallback(() => {
    resetForm();
  }, [resetForm]);

  if (loader) {
    return <DefaultLoader />;
  }

  return (
    <Container>
      {showSearch && (
        <Search
          newData={() => setShowSearch(false)}
          onRowClick={onRowClick}
          codTela={82}
        />
      )}

      {!showSearch && (
        <FormDefault
          codTela={82}
          title="Cadastro de Status PDV"
          codigoRegistro={[{ value: valStatusValue, des_campo: 'Status' }]}
          onSave={rhfSubmit(onSubmit)}
          onCancel={() => {
            resetForm();
            setShowSearch(true);
          }}
          isUpdate={isUpdate}
          onNew={onNew}
          onDelete={handleDelete}
          onClearFields={resetForm}
          onReturnSearch={() => {
            setShowSearch(true);
            setIsUpdate(false);
            resetForm();
          }}
        >
          <div className="row">
            <div className="col-sm-12 col-md-10">
              <InputText
                label="Descrição"
                name="des_status"
                maxLength={50}
                placeholder="Informe a Descrição"
                register={register}
                control={control}
                isError={!!errors.des_status}
                onInput={(ev: any) => {
                  const input = ev.target;
                  const start = input.selectionStart ?? input.value.length;
                  const end = input.selectionEnd ?? input.value.length;

                  // apenas UPPERCASE enquanto digita
                  const upper = input.value.toUpperCase();

                  setValue('des_status', upper, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  // restaura a posição do cursor
                  requestAnimationFrame(() => {
                    try {
                      input.setSelectionRange(start, end);
                    } catch {
                      /* input pode não estar montado; ignore */
                    }
                  });
                }}
              />
            </div>

            <div className="col-sm-12 col-md-2">
              <InputNumber
                label="Status PDV"
                name="val_status"
                step="1"
                min={0}
                max={99}
                maxLength={3}
                placeholder=""
                register={register}
                control={control}
                isError={!!errors.val_status}
                onInput={(ev: any) => {
                  const { value } = ev.target;

                  setValue('val_status', Number(value), {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            </div>

            <div className="col-sm-12 col-md-2 mt-2">
              <ToggleDefault
                size="large"
                labelText="Negativar Cliente?"
                setChecked={!!negativar}
                onSwitch={() =>
                  setValue('negativar_status', !negativar, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
            </div>

            <div className="col-sm-12 col-md-4 mt-1">
              <InputSelect
                options={options}
                label="Condição de Aceite"
                name="indicador_status"
                placeholder="Informe a Condição"
                register={register}
                control={control}
                isError={!!errors.indicador_status}
                changeSelected={(opt: Option) =>
                  setValue('indicador_status', opt, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
            </div>

            <div className="col-sm-12 col-md-2 mt-1">
              <InputSelect
                options={options_finalizadora}
                label="Tipo de Finalizadora"
                name="tipo_status"
                placeholder="Informe o Tipo de Finalizadora"
                register={register}
                control={control}
                isError={!!errors.tipo_status}
                changeSelected={(opt: Option) =>
                  setValue('tipo_status', opt, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
            </div>
          </div>
        </FormDefault>
      )}
    </Container>
  );
};

export default CadastroStatusPdv;
